import React, { PropsWithChildren } from "react";
import { checkUserHasAnyInCategory, checkUserHasPermission } from "./UserPermittedSlice";
import { PermissionCategories, Permissions } from "cpl-react-common/components/Enums/Permissions";
import { IAuthState } from "app/modules/auth";
import { useSelector, shallowEqual } from "react-redux";
import { Navigate } from "react-router-dom";
import { RootState } from "setup";

type PermissionProps = {
  permissionPredicate: (permission: Permissions) => boolean;
};

type PermittedRouteProps = {
  permission: Permissions;
};

type PermissionCategoryProps = {
  permissionCategoryPredicate: (permissionCategory: PermissionCategories) => boolean;
};

export const Permission: React.FC<PropsWithChildren<PermissionProps>> = ({ permissionPredicate, children }) => {
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  const permitted = checkUserHasPermission(permissionPredicate);

  return <>{(permitted || authState.user?.superUser) && children}</>;
};

export const useUserHasPermission = (permissionPredicate: (permission: Permissions) => boolean): boolean => {
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  return !!authState.user?.superUser || checkUserHasPermission(permissionPredicate);
};

/**
 * Route-level guard: renders the page only when the user holds the given permission (or is a super user),
 * otherwise redirects to the 403 page. Use this in route definitions so pages hidden from the navigation
 * menu cannot be reached by entering the URL directly.
 */
export const PermittedRoute: React.FC<PropsWithChildren<PermittedRouteProps>> = ({ permission, children }) => {
  const permitted = useUserHasPermission((userPermission) => userPermission === permission);

  return permitted ? <>{children}</> : <Navigate to="/error/403" replace />;
};

export const PermissionCategory: React.FC<PropsWithChildren<PermissionCategoryProps>> = ({
  permissionCategoryPredicate,
  children,
}) => {
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  const permitted = checkUserHasAnyInCategory(permissionCategoryPredicate);

  return <>{(permitted || authState.user?.superUser) && children}</>;
};
